import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Loader2, Plus, Trash2, Save } from "lucide-react";

function QAList({ title, items, onChange, prefix }) {
  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  return (
    <div className="glass-panel p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-lg tracking-tight">{title}</h2>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => onChange([...items, { q: "", a: "" }])}
          data-testid={`${prefix}-add`}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="rounded-2xl border border-white/60 bg-white/50 p-3 space-y-2">
            <div className="flex gap-2">
              <Input
                value={it.q}
                onChange={(e) => update(i, "q", e.target.value)}
                placeholder="Question"
                className="font-medium"
                data-testid={`${prefix}-q-${i}`}
              />
              <button
                onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                className="shrink-0 w-10 h-10 rounded-xl border border-red-200 text-red-500 flex items-center justify-center hover:bg-red-50"
                data-testid={`${prefix}-remove-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <Textarea
              value={it.a}
              onChange={(e) => update(i, "a", e.target.value)}
              placeholder="Answer (line breaks are kept)"
              rows={3}
              data-testid={`${prefix}-a-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FAQEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/faq")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-neutral-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading FAQ…
      </div>
    );
  }

  const save = async () => {
    const clean = {
      ...data,
      faqs: data.faqs.filter((x) => x.q.trim()),
      rules: data.rules.filter((x) => x.q.trim()),
      do_not_approach: data.do_not_approach.filter((x) => x.trim()),
    };
    setSaving(true);
    try {
      await api.put("/admin/faq", clean);
      setData(clean);
      toast.success("FAQ saved — promoters see it instantly");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-500">
          Everything below appears on the promoter FAQ page. Line breaks are preserved.
        </p>
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-slate-900 hover:bg-slate-700 text-white"
          data-testid="faq-save-button"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1" /> Save changes</>)}
        </Button>
      </div>

      <div className="glass-panel p-4 sm:p-5">
        <Label className="text-xs uppercase tracking-wider">Do not approach (one per line)</Label>
        <Textarea
          className="mt-2"
          rows={3}
          value={data.do_not_approach.join("\n")}
          onChange={(e) => setData({ ...data, do_not_approach: e.target.value.split("\n") })}
          data-testid="faq-dna-textarea"
        />
      </div>

      <QAList
        title="FAQ questions"
        items={data.faqs}
        onChange={(faqs) => setData({ ...data, faqs })}
        prefix="faq-editor"
      />
      <QAList
        title="Communication rules"
        items={data.rules}
        onChange={(rules) => setData({ ...data, rules })}
        prefix="rules-editor"
      />

      <div className="glass-panel p-4 sm:p-5 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">Grooming</Label>
          <Textarea
            className="mt-2"
            rows={2}
            value={data.grooming}
            onChange={(e) => setData({ ...data, grooming: e.target.value })}
            data-testid="faq-grooming-textarea"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Always remember</Label>
          <Textarea
            className="mt-2"
            rows={2}
            value={data.always_remember}
            onChange={(e) => setData({ ...data, always_remember: e.target.value })}
            data-testid="faq-remember-textarea"
          />
        </div>
      </div>
    </div>
  );
}
